import { isEthiopianLeapYear, toEthiopian, toGregorian } from 'ethiopian-calendar-new';
import { CalendarSystem } from '@/utils/enums';

export type DatePreset = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'custom';

export const DATE_PRESETS: Array<{ value: DatePreset; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'thisWeek', label: 'This week' },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
];

/** Local calendar date -> YYYY-MM-DD (no timezone shifting). */
export const toDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** YYYY-MM-DD -> local Date at midnight. */
export const fromDateKey = (key: string): Date => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date: Date, n: number) => {
  const d = startOfDay(date);
  d.setDate(d.getDate() + n);
  return d;
};

const ethiopicMonthBounds = (year: number, month: number) => {
  const days = month <= 12 ? 30 : isEthiopianLeapYear(year) ? 6 : 5;
  const startGreg = toGregorian(year, month, 1);
  const endGreg = toGregorian(year, month, days);
  return {
    start: new Date(startGreg.year, startGreg.month - 1, startGreg.day),
    end: new Date(endGreg.year, endGreg.month - 1, endGreg.day),
  };
};

/** Resolve a preset to an inclusive local date range that respects the org calendar. */
export const presetRange = (preset: DatePreset, calendarSystem: string): { start: Date; end: Date } => {
  const today = startOfDay(new Date());
  switch (preset) {
    case 'today':
      return { start: today, end: today };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { start: y, end: y };
    }
    case 'thisWeek': {
      const diffToMonday = (today.getDay() + 6) % 7;
      const start = addDays(today, -diffToMonday);
      return { start, end: addDays(start, 6) };
    }
    case 'lastWeek': {
      const diffToMonday = (today.getDay() + 6) % 7;
      const start = addDays(today, -diffToMonday - 7);
      return { start, end: addDays(start, 6) };
    }
    case 'thisMonth': {
      if (calendarSystem === CalendarSystem.ETHIOPIC) {
        const eth = toEthiopian(today.getFullYear(), today.getMonth() + 1, today.getDate());
        return ethiopicMonthBounds(eth.year, eth.month);
      }
      return { start: new Date(today.getFullYear(), today.getMonth(), 1), end: new Date(today.getFullYear(), today.getMonth() + 1, 0) };
    }
    case 'lastMonth': {
      if (calendarSystem === CalendarSystem.ETHIOPIC) {
        const eth = toEthiopian(today.getFullYear(), today.getMonth() + 1, today.getDate());
        const month = eth.month === 1 ? 13 : eth.month - 1;
        const year = eth.month === 1 ? eth.year - 1 : eth.year;
        return ethiopicMonthBounds(year, month);
      }
      return { start: new Date(today.getFullYear(), today.getMonth() - 1, 1), end: new Date(today.getFullYear(), today.getMonth(), 0) };
    }
    default:
      return { start: today, end: today };
  }
};

export const rangeDays = (start: Date, end: Date): number => Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / 86400000) + 1;

/** Trigger a browser download for a CSV blob returned by the API. */
export const downloadBlob = (data: Blob | string, filename: string) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/** Read the filename from a Content-Disposition header, if present. */
export const filenameFromHeaders = (headers: Record<string, any> | undefined, fallback: string): string => {
  const disposition = headers?.['content-disposition'] ?? headers?.['Content-Disposition'];
  const match = disposition ? /filename="?([^";]+)"?/.exec(String(disposition)) : null;
  return match ? match[1] : fallback;
};
